"use client"

import { useState } from "react"
import {
  ArrowLeft,
  Download,
  Share2,
  Bookmark,
  Calendar,
  Clock,
  Eye,
  TrendingUp,
  Target,
  BarChart3,
  FileText,
  Star,
  CheckCircle,
  AlertTriangle,
  Info,
} from "lucide-react"
import "./DetailedPreGeneratedReportPage.css"
import BackButton from "../shared/backButton";

const DetailedPreGeneratedReportPage = ({ reportData }) => {
  const [activeSection, setActiveSection] = useState("overview")

  // Sample detailed report data
  const getReportDetails = () => {
    if (!reportData) {
      return getDefaultReportData()
    }

    return {
      ...reportData,
      sections: {
        overview: {
          title: "Executive Summary",
          content: `This comprehensive ${reportData.title.toLowerCase()} provides detailed insights into organizational performance metrics and strategic recommendations. The analysis covers key performance indicators, trend analysis, and actionable insights for decision-making.`,
          keyMetrics: [
            { label: "Total Employees Analyzed", value: "156", trend: "up" },
            { label: "Performance Score", value: "87%", trend: "up" },
            { label: "Engagement Level", value: "4.2/5", trend: "stable" },
            { label: "Retention Rate", value: "91%", trend: "down" },
          ],
        },
        analysis: {
          title: "Detailed Analysis",
          charts: [
            {
              title: "Performance Distribution",
              type: "bar",
              data: [
                { label: "Excellent (90-100%)", value: 23, color: "#10b981" },
                { label: "Good (80-89%)", value: 45, color: "#3b82f6" },
                { label: "Average (70-79%)", value: 67, color: "#f59e0b" },
                { label: "Below Average (<70%)", value: 21, color: "#ef4444" },
              ],
            },
            {
              title: "Department Comparison",
              type: "line",
              data: [
                { label: "Engineering", value: 92 },
                { label: "Product", value: 89 },
                { label: "Sales", value: 85 },
                { label: "Marketing", value: 87 },
                { label: "Support", value: 83 },
              ],
            },
          ],
        },
        insights: {
          title: "Key Insights & Findings",
          findings: [
            {
              type: "positive",
              title: "Strong Technical Performance",
              description: "Engineering teams consistently outperform benchmarks with 92% average performance score.",
              impact: "High",
            },
            {
              type: "warning",
              title: "Retention Concerns in Sales",
              description: "Sales department shows 15% higher turnover rate compared to company average.",
              impact: "Medium",
            },
            {
              type: "info",
              title: "Training Effectiveness",
              description: "Employees who completed training programs show 23% better performance scores.",
              impact: "High",
            },
            {
              type: "positive",
              title: "Leadership Pipeline Strength",
              description: "35% of high performers demonstrate strong leadership potential for succession planning.",
              impact: "High",
            },
          ],
        },
        recommendations: {
          title: "Strategic Recommendations",
          items: [
            {
              priority: "high",
              title: "Implement Retention Program for Sales Team",
              description:
                "Develop targeted retention strategies including career development paths and competitive compensation review.",
              timeline: "3 months",
              expectedImpact: "Reduce turnover by 25%",
            },
            {
              priority: "medium",
              title: "Expand Training Programs",
              description: "Scale successful training initiatives to underperforming departments based on proven ROI.",
              timeline: "6 months",
              expectedImpact: "Improve overall performance by 15%",
            },
            {
              priority: "high",
              title: "Leadership Development Initiative",
              description: "Create structured leadership development program for identified high-potential employees.",
              timeline: "12 months",
              expectedImpact: "Build internal succession pipeline",
            },
          ],
        },
        methodology: {
          title: "Methodology & Data Sources",
          content: `This report analyzes data from multiple sources including performance reviews, engagement surveys, training records, and HR metrics. The analysis period covers the last 12 months with data points collected monthly. Statistical methods include trend analysis, correlation studies, and comparative benchmarking against industry standards.`,
          dataSources: [
            "Performance Management System",
            "Employee Engagement Surveys",
            "Training & Development Records",
            "HR Information System",
            "Industry Benchmark Data",
          ],
        },
      },
    }
  }

  const getDefaultReportData = () => {
    return {
      id: 1,
      title: "Top 20 High Performers Analysis",
      description: "Comprehensive analysis of top-performing employees across all departments",
      category: "Employee Reports",
      type: "Summary",
      lastGenerated: "2024-01-22",
      downloads: 45,
      views: 234,
      estimatedTime: "5 min read",
      sections: {
        overview: {
          title: "Executive Summary",
          content:
            "This comprehensive analysis provides detailed insights into our top-performing employees, identifying key success factors and patterns that drive exceptional performance across the organization.",
          keyMetrics: [
            { label: "Top Performers Identified", value: "20", trend: "up" },
            { label: "Average Performance Score", value: "94%", trend: "up" },
            { label: "Retention Rate", value: "96%", trend: "stable" },
            { label: "Promotion Readiness", value: "8", trend: "up" },
          ],
        },
        analysis: {
          title: "Performance Analysis",
          charts: [
            {
              title: "Performance Distribution",
              type: "bar",
              data: [
                { label: "Exceptional (95-100%)", value: 8, color: "#10b981" },
                { label: "Excellent (90-94%)", value: 12, color: "#3b82f6" },
              ],
            },
          ],
        },
        insights: {
          title: "Key Insights & Findings",
          findings: [
            {
              type: "positive",
              title: "Consistent High Performance",
              description: "Top performers maintain 94% average performance score over 12-month period.",
              impact: "High",
            },
            {
              type: "info",
              title: "Leadership Potential",
              description: "40% of top performers show strong leadership capabilities and mentoring skills.",
              impact: "High",
            },
          ],
        },
        recommendations: {
          title: "Strategic Recommendations",
          items: [
            {
              priority: "high",
              title: "Retention Strategy Implementation",
              description:
                "Develop comprehensive retention program for top performers including career advancement and recognition.",
              timeline: "2 months",
              expectedImpact: "Maintain 95%+ retention rate",
            },
          ],
        },
        methodology: {
          title: "Methodology & Data Sources",
          content:
            "Analysis based on performance reviews, peer feedback, and achievement metrics over 12-month period.",
          dataSources: ["Performance Management System", "Peer Review Data", "Achievement Records"],
        },
      },
    }
  }

  const reportDetails = getReportDetails()

  const sections = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "analysis", label: "Analysis", icon: TrendingUp },
    { id: "insights", label: "Insights", icon: Star },
    { id: "recommendations", label: "Recommendations", icon: Target },
    { id: "methodology", label: "Methodology", icon: FileText },
  ]

  const handleDownload = () => {
    // Simulate PDF download
    const element = document.createElement("a")
    const file = new Blob(
      [
        `${reportDetails.title}\n\nGenerated on: ${new Date().toLocaleDateString()}\n\n${reportDetails.sections.overview.content}\n\nThis is a sample PDF download. In a real implementation, this would generate a comprehensive PDF report with all analysis data, charts, and recommendations.`,
      ],
      { type: "text/plain" },
    )
    element.href = URL.createObjectURL(file)
    element.download = `${reportDetails.title.replace(/\s+/g, "-").toLowerCase()}-report-${Date.now()}.txt`
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: reportDetails.title,
        text: reportDetails.description,
        url: window.location.href,
      })
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(window.location.href)
      alert("Report link copied to clipboard!")
    }
  }

  const getTrendIcon = (trend) => {
    switch (trend) {
      case "up":
        return <TrendingUp size={16} className="detailed-report-trend-up" />
      case "down":
        return <TrendingUp size={16} className="detailed-report-trend-down" style={{ transform: "rotate(180deg)" }} />
      default:
        return <Target size={16} className="detailed-report-trend-stable" />
    }
  }

  const getInsightIcon = (type) => {
    switch (type) {
      case "positive":
        return <CheckCircle size={20} className="detailed-report-insight-positive" />
      case "warning":
        return <AlertTriangle size={20} className="detailed-report-insight-warning" />
      case "info":
        return <Info size={20} className="detailed-report-insight-info" />
      default:
        return <Info size={20} className="detailed-report-insight-info" />
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "detailed-report-priority-high"
      case "medium":
        return "detailed-report-priority-medium"
      case "low":
        return "detailed-report-priority-low"
      default:
        return "detailed-report-priority-medium"
    }
  }

  const renderSection = () => {
    switch (activeSection) {
      case "overview":
        return (
          <div className="detailed-report-section-content">
            <div className="detailed-report-overview-content">
              <h3>{reportDetails.sections.overview.title}</h3>
              <p>{reportDetails.sections.overview.content}</p>
            </div>

            <div className="detailed-report-metrics-grid">
              {reportDetails.sections.overview.keyMetrics.map((metric, index) => (
                <div key={index} className="detailed-report-metric-card">
                  <div className="detailed-report-metric-header">
                    <span className="detailed-report-metric-label">{metric.label}</span>
                    {getTrendIcon(metric.trend)}
                  </div>
                  <div className="detailed-report-metric-value">{metric.value}</div>
                </div>
              ))}
            </div>
          </div>
        )

      case "analysis":
        return (
          <div className="detailed-report-section-content">
            <h3>{reportDetails.sections.analysis.title}</h3>
            <div className="detailed-report-charts-container">
              {reportDetails.sections.analysis.charts?.map((chart, index) => (
                <div key={index} className="detailed-report-chart-card">
                  <h4>{chart.title}</h4>
                  {chart.type === "bar" && (
                    <div className="detailed-report-bar-chart">
                      {chart.data.map((item, i) => (
                        <div key={i} className="detailed-report-bar-item">
                          <div className="detailed-report-bar-label">{item.label}</div>
                          <div className="detailed-report-bar-container">
                            <div
                              className="detailed-report-bar-fill"
                              style={{
                                width: `${(item.value / Math.max(...chart.data.map((d) => d.value))) * 100}%`,
                                backgroundColor: "#0e1822",
                              }}
                            ></div>
                            <span className="detailed-report-bar-value">{item.value}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {chart.type === "line" && (
                    <div className="detailed-report-line-chart">
                      {chart.data.map((item, i) => (
                        <div key={i} className="detailed-report-line-item">
                          <span className="detailed-report-line-label">{item.label}</span>
                          <span className="detailed-report-line-value">{item.value}%</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )

      case "insights":
        return (
          <div className="detailed-report-section-content">
            <h3>{reportDetails.sections.insights.title}</h3>
            <div className="detailed-report-insights-container">
              {reportDetails.sections.insights.findings.map((finding, index) => (
                <div key={index} className={`detailed-report-insight-card detailed-report-insight-${finding.type}`}>
                  <div className="detailed-report-insight-header">
                    {getInsightIcon(finding.type)}
                    <div className="detailed-report-insight-title-section">
                      <h4>{finding.title}</h4>
                      <span className="detailed-report-insight-impact">Impact: {finding.impact}</span>
                    </div>
                  </div>
                  <p>{finding.description}</p>
                </div>
              ))}
            </div>
          </div>
        )

      case "recommendations":
        return (
          <div className="detailed-report-section-content">
            <h3>{reportDetails.sections.recommendations.title}</h3>
            <div className="detailed-report-recommendations-container">
              {reportDetails.sections.recommendations.items.map((rec, index) => (
                <div key={index} className="detailed-report-recommendation-card">
                  <div className="detailed-report-recommendation-header">
                    <h4>{rec.title}</h4>
                    <span className={`detailed-report-priority-badge ${getPriorityColor(rec.priority)}`}>
                      {rec.priority} priority
                    </span>
                  </div>
                  <p>{rec.description}</p>
                  <div className="detailed-report-recommendation-details">
                    <div className="detailed-report-recommendation-detail">
                      <Clock size={16} />
                      <span>Timeline: {rec.timeline}</span>
                    </div>
                    <div className="detailed-report-recommendation-detail">
                      <Target size={16} />
                      <span>Expected Impact: {rec.expectedImpact}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )

      case "methodology":
        return (
          <div className="detailed-report-section-content">
            <h3>{reportDetails.sections.methodology.title}</h3>
            <div className="detailed-report-methodology-content">
              <p>{reportDetails.sections.methodology.content}</p>
              <div className="detailed-report-data-sources">
                <h4>Data Sources:</h4>
                <ul>
                  {reportDetails.sections.methodology.dataSources.map((source, index) => (
                    <li key={index}>{source}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )

      default:
        return <div>Select a section to view details</div>
    }
  }

  return (
    <div className="detailed-pregenerated-report-page">
      <BackButton />

      <main className="detailed-pregenerated-report-main">
      <h1 style={{textAlign: "center", fontWeight: "600", marginBottom: "10px"}}>REPORT</h1>
        <div className="container">
          {/* Header */}
          <div className="detailed-pregenerated-report-header">
            <div className="detailed-report-header-content">
              <div className="detailed-report-header-icon">
                <FileText size={40} />
              </div>
              <div className="detailed-report-header-text">
                <h1>{reportDetails.title}</h1>
                <p>{reportDetails.description}</p>
                <div className="detailed-report-header-meta">
                  <div className="detailed-report-meta-item">
                    <Calendar size={16} />
                    <span>Generated: {new Date(reportDetails.lastGenerated).toLocaleDateString()}</span>
                  </div>
                  <div className="detailed-report-meta-item">
                    <Clock size={16} />
                    <span>{reportDetails.estimatedTime}</span>
                  </div>
                  <div className="detailed-report-meta-item">
                    <Eye size={16} />
                    <span>{reportDetails.views} views</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="detailed-report-header-actions">
              <button className="detailed-report-action-btn" onClick={handleShare}>
                <Share2 size={16} />
                Share
              </button>
              <button className="detailed-report-action-btn">
                <Bookmark size={16} />
                Save
              </button>
              <button className="detailed-report-download-btn" onClick={handleDownload}>
                <Download size={16} />
                Download PDF
              </button>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="detailed-report-section-navigation">
            {sections.map((section) => {
              const IconComponent = section.icon
              return (
                <button
                  key={section.id}
                  className={`detailed-report-nav-tab ${activeSection === section.id ? "detailed-report-nav-active" : ""}`}
                  onClick={() => setActiveSection(section.id)}
                >
                  <IconComponent size={16} />
                  <span>{section.label}</span>
                </button>
              )
            })}
          </div>

          {/* Content Area */}
          <div className="detailed-report-content-area">{renderSection()}</div>
        </div>
      </main>
    </div>
  )
}

export default DetailedPreGeneratedReportPage
